/**
 * Query-key registry.
 *
 * Every key in the app is built here. Keys declared inline at call sites drift, and
 * then an invalidation silently stops matching the query it was meant to refresh —
 * which looks exactly like a caching bug and is very hard to find.
 *
 * Convention: `[domain, resource, ...params]`, narrowest last, so a prefix
 * invalidation (`queryKeys.projects.all`) clears everything beneath it.
 */

export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },


  systemHealth: {
    all: ['system-health'] as const,
    providerUsage: () => ['system-health', 'provider-usage'] as const,
    balanceCapability: () => ['system-health', 'balance-capability'] as const,
    alertSummary: () => ['system-health', 'alert-summary'] as const,
    capabilities: () => ['system-health', 'capabilities'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    analytics: () => ['dashboard', 'analytics'] as const,
  },

  projects: {
    all: ['projects'] as const,
    list: (params: { projectType?: string | undefined; limit?: number | undefined }) =>
      ['projects', 'list', params] as const,
    detail: (projectId: string) => ['projects', 'detail', projectId] as const,
    summary: (projectId: string) => ['projects', 'summary', projectId] as const,
    page: (params: unknown) => ['projects', 'page', params] as const,
    stats: () => ['projects', 'stats'] as const,
    importSummary: () => ['projects', 'import-summary'] as const,
  },

  people: {
    all: ['people'] as const,
    list: (query: unknown) => ['people', 'list', query] as const,
    counts: (query: unknown) => ['people', 'counts', query] as const,
    filterOptions: (params: unknown) =>
      ['people', 'filter-options', params] as const,
    enrichmentHistory: (id: number) => ['people', 'enrichment-history', id] as const,
  },

  organisations: {
    all: ['organisations'] as const,
    list: (query: unknown) => ['organisations', 'list', query] as const,
    counts: (query: unknown) => ['organisations', 'counts', query] as const,
    matchCounts: (query: unknown) => ['organisations', 'match-counts', query] as const,
    filterOptions: (params: unknown) =>
      ['organisations', 'filter-options', params] as const,
  },

  outreach: {
    all: ['outreach'] as const,
    overview: (period: string, projectId: string | null = null) =>
      ['outreach', 'overview', period, projectId] as const,
    breakdown: (period: string) => ['outreach', 'breakdown', period] as const,
    seenMailboxes: () => ['outreach', 'seen-mailboxes'] as const,
    campaign: (id: string, tab: string, params: unknown = null) => ['outreach', 'campaign', id, tab, params] as const,
    campaigns: (page: number, pageSize = 25, filters: unknown = null) =>
      ['outreach', 'campaigns', page, pageSize, filters] as const,
    inbox: (filters: unknown, page: number) => ['outreach', 'inbox', 'list', filters, page] as const,
    inboxCounts: () => ['outreach', 'inbox', 'counts'] as const,
    inboxAssignees: (key: string) => ['outreach', 'inbox', 'assignees', key] as const,
    inboxAll: () => ['outreach', 'inbox'] as const,
    templates: (purpose: string, q: string, page: number) => ['outreach', 'templates', 'list', purpose, q, page] as const,
    template: (ref: string) => ['outreach', 'templates', 'detail', ref] as const,
    templatesAll: () => ['outreach', 'templates'] as const,
    mktList: (q: string, status: string, page: number) => ['outreach', 'mkt', 'list', q, status, page] as const,
    mktDetail: (id: number) => ['outreach', 'mkt', 'detail', id] as const,
    mktAudience: (id: number, page: number) => ['outreach', 'mkt', 'audience', id, page] as const,
    mktSnapshot: (id: number, snapshotId: number) => ['outreach', 'mkt', 'snapshot', id, snapshotId] as const,
    mktSnapshots: (id: number) => ['outreach', 'mkt', 'snapshots', id] as const,
    mktSchedule: (id: number) => ['outreach', 'mkt', 'schedule', id] as const,
    limits: () => ['outreach', 'limits'] as const,
  },

    jobOpportunities: {
    all: ['job-opportunities'] as const,
    overview: () => ['job-opportunities', 'overview'] as const,
    overviewKPIs: (params: unknown) => ['job-opportunities', 'overview-kpis', params] as const,
    overviewAttention: (params: unknown) => ['job-opportunities', 'overview-attention', params] as const,
    overviewIntelligence: (params: unknown) => ['job-opportunities', 'overview-intelligence', params] as const,
    overviewOutreach: (params: unknown) => ['job-opportunities', 'overview-outreach', params] as const,
    overviewStakeholders: (params: unknown) => ['job-opportunities', 'overview-stakeholders', params] as const,
    list: (query: unknown) => ['job-opportunities', 'list', query] as const,
    summary: (projectId: string) => ['job-opportunities', 'summary', projectId] as const,
    detail: (projectId: string, opportunityId: string) =>
      ['job-opportunities', 'detail', projectId, opportunityId] as const,
    policyShadow: (projectId: string, opportunityId: string) =>
      ['job-opportunities', 'policy-shadow', projectId, opportunityId] as const,
    runs: (projectId: string, page: number | string) =>
      ['job-opportunities', 'runs', projectId, page] as const,
    schedules: (projectId: string) => ['job-opportunities', 'schedules', projectId] as const,
    reviews: (projectId: string, opportunityId: string) =>
      ['job-opportunities', 'reviews', projectId, opportunityId] as const,
    stakeholders: (projectId: string, opportunityId: string) =>
      ['job-opportunities', 'stakeholders', projectId, opportunityId] as const,
    companyPeople: (projectId: string, opportunityId: string, query: unknown) =>
      ['job-opportunities', 'company-people', projectId, opportunityId, query] as const,
    outreachTemplates: (params: unknown) => ['job-opportunities', 'outreach-templates', params] as const,
    outreachTemplateDetail: (templateId: string) =>
      ['job-opportunities', 'outreach-template-detail', templateId] as const,
    duplicatePair: (projectId: string, opportunityId: string) =>
      ['job-opportunities', 'duplicate-pair', projectId, opportunityId] as const,
    presentationAssets: () => ['job-opportunities', 'presentation-assets'] as const,
  },

  imports: {
    all: ['imports'] as const,
    jobs: (limit: number) => ['imports', 'jobs', limit] as const,
    job: (jobId: string) => ['imports', 'job', jobId] as const,
  },

  compliance: {
    all: ['compliance'] as const,
    options: () => ['compliance', 'options'] as const,
    approvals: () => ['compliance', 'approvals'] as const,
    history: (params: unknown) => ['compliance', 'history', params] as const,
    consent: (personId: number) => ['compliance', 'consent', personId] as const,
  },
} as const;
